import React from 'react'
import { t } from 'i18next'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import FilterSelector from 'Components/FilterSelector'
import PhoneNumberField from 'Components/PhoneNumberField'
import CatalogImageInput from './CatalogImageInput'
import {
  getTodayDateString,
  matchesPriceListSearch,
} from '../utils/quickListCatalog.utils'

const priceListLabel = list =>
  [list.Code, list.Description].filter(Boolean).join(' - ')
const toTestId = name =>
  name.replace(/[A-Z]/g, char => `-${char.toLowerCase()}`)

function FieldError({ formik, name }) {
  if (!formik.touched[name] || !formik.errors[name]) return null
  return (
    <div className="input-feedback">
      <strong data-testid={`text-error-${toTestId(name)}-catalog`}>
        {formik.errors[name]}
      </strong>
    </div>
  )
}

function TextField({ formik, name, label, placeholder, type = 'text' }) {
  const hasError = formik.touched[name] && formik.errors[name]
  return (
    <div className="form-group">
      <label htmlFor={name}>
        {label}
        <span className="required">*</span>
      </label>
      <input
        type={type}
        id={name}
        name={name}
        data-testid={`input-${toTestId(name)}-catalog`}
        className={hasError ? 'form-control error' : 'form-control'}
        placeholder={placeholder}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      />
      <FieldError formik={formik} name={name} />
    </div>
  )
}

function CatalogGeneralStep({ formik, priceLists, entityId }) {
  const priceListGroups = [
    { key: 'base', label: t('QUICK_LIST_CATALOG.GENERAL.PRICE_LIST_BASE') },
    {
      key: 'regular',
      label: t('QUICK_LIST_CATALOG.GENERAL.PRICE_LIST_REGULAR'),
    },
    {
      key: 'catalog',
      label: t('QUICK_LIST_CATALOG.GENERAL.PRICE_LIST_CATALOG'),
    },
    {
      key: 'discountMatrix',
      label: t('QUICK_LIST_CATALOG.GENERAL.PRICE_LIST_DISCOUNT_MATRIX'),
    },
  ]

  // Una lista puede venir en más de un grupo; se muestra solo en el primero.
  const seenPriceListIds = new Set()
  const priceListOptions = priceListGroups.flatMap(group =>
    (priceLists[group.key] || [])
      .map(list => ({
        ...list,
        priceListId: list.PriceListId?.toLowerCase(),
        groupLabel: group.label,
      }))
      .filter(list => {
        if (!list.priceListId || seenPriceListIds.has(list.priceListId))
          return false
        seenPriceListIds.add(list.priceListId)
        return true
      })
  )

  const priceListError = formik.touched.priceListId && formik.errors.priceListId

  return (
    <div className="catalog-step" data-testid="container-step-general">
      <Row>
        <Col md={12} lg={6}>
          <TextField
            formik={formik}
            name="name"
            label={t('QUICK_LIST_CATALOG.GENERAL.NAME')}
            placeholder={t('QUICK_LIST_CATALOG.GENERAL.PLACEHOLDER_NAME')}
          />

          <div className="form-group">
            <label htmlFor="selector-price-list">
              {t('QUICK_LIST_CATALOG.GENERAL.PRICE_LIST')}
              <span className="required">*</span>
            </label>
            <FilterSelector
              // Se muestra cuando la lista guardada no está entre las opciones de la compañía.
              label={t('QUICK_LIST_CATALOG.GENERAL.PRICE_LIST_UNAVAILABLE')}
              options={priceListOptions}
              getOptionValue={item => item.priceListId}
              getOptionLabel={priceListLabel}
              getOptionGroup={item => item.groupLabel}
              value={formik.values.priceListId}
              onChange={item => {
                formik.setFieldValue('priceListId', item.priceListId)
                formik.setFieldTouched('priceListId', true, false)
              }}
              placeholder={t('QUICK_LIST_CATALOG.GENERAL.SELECT_PRICE_LIST')}
              searchFunction={matchesPriceListSearch}
              searchPlaceholder={t(
                'QUICK_LIST_CATALOG.GENERAL.SEARCH_PRICE_LIST'
              )}
              noResultsText={t('QUICK_LIST_CATALOG.GENERAL.NO_PRICE_LISTS')}
              hasError={Boolean(priceListError)}
              testId="price-list"
            />
            <p className="catalog-help">
              {t('QUICK_LIST_CATALOG.GENERAL.PRICE_LIST_HELP')}
            </p>
            <FieldError formik={formik} name="priceListId" />
          </div>

          <div className="form-group">
            <label htmlFor="endDate">
              {t('QUICK_LIST_CATALOG.GENERAL.END_DATE')}
              <span className="required">*</span>
            </label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              data-testid="input-end-date-catalog"
              min={getTodayDateString()}
              className={
                formik.touched.endDate && formik.errors.endDate
                  ? 'form-control error'
                  : 'form-control'
              }
              value={formik.values.endDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <p className="catalog-help">
              {t('QUICK_LIST_CATALOG.GENERAL.END_DATE_HELP')}
            </p>
            <FieldError formik={formik} name="endDate" />
          </div>

          <h3 className="catalog-subtitle">
            {t('QUICK_LIST_CATALOG.GENERAL.CONTACT')}
          </h3>
          <Row>
            <Col md={6}>
              <div className="form-group">
                <label htmlFor="contactPhone">
                  {t('QUICK_LIST_CATALOG.GENERAL.PHONE')}
                  <span className="required">*</span>
                </label>
                <PhoneNumberField
                  id="contactPhone"
                  formik={formik}
                  fieldName="contactPhone"
                  error={Boolean(
                    formik.touched.contactPhone && formik.errors.contactPhone
                  )}
                  testId="contact-phone-catalog"
                />
                <FieldError formik={formik} name="contactPhone" />
              </div>
            </Col>
            <Col md={6}>
              <TextField
                formik={formik}
                name="contactEmail"
                type="email"
                label={t('QUICK_LIST_CATALOG.GENERAL.EMAIL')}
                placeholder={t('QUICK_LIST_CATALOG.GENERAL.PLACEHOLDER_EMAIL')}
              />
            </Col>
          </Row>
        </Col>

        <Col md={12} lg={6}>
          <CatalogImageInput
            id="coverImage"
            label={t('QUICK_LIST_CATALOG.GENERAL.COVER')}
            value={formik.values.coverImageUrl}
            entityId={entityId}
            onChange={url => formik.setFieldValue('coverImageUrl', url)}
            testId="catalog-cover"
          />
        </Col>
      </Row>
    </div>
  )
}

export default CatalogGeneralStep
